/**
 * The pictures a class can put behind its sign-in page.
 *
 * Six of them ship with the product, served from this origin — which is what the
 * content policy allows, and what makes them load instantly. A class that wants its own
 * photograph uploads one instead (Class settings → Your sign-in page), and it is served
 * from here too.
 *
 * `colour` is the average colour of each picture: the page paints it underneath, so
 * there is never a white flash before the image arrives. `tone` says which way the
 * scrim goes — a dark picture is dimmed, a light one is washed — so the card and its
 * text stay readable whichever is chosen.
 */

export interface Background {
  id: string;
  label: string;
  /** What it feels like, in the instructor's words. */
  description: string;
  /** `code` is drawn in the page; `picture` is a file we serve. */
  kind: 'code' | 'picture';
  colour: string;
  tone: 'dark' | 'light';
}

export const BACKGROUNDS: readonly Background[] = [
  {
    id: 'code',
    label: 'Code',
    description: 'The page every student starts from, faintly, behind the window.',
    kind: 'code',
    colour: '#080a12',
    tone: 'dark',
  },
  {
    id: 'aurora',
    label: 'Aurora',
    description: 'Deep indigo, with a ribbon of light.',
    kind: 'picture',
    colour: '#131036',
    tone: 'dark',
  },
  {
    id: 'horizon',
    label: 'Horizon',
    description: 'A grid running out to a sunrise.',
    kind: 'picture',
    colour: '#191925',
    tone: 'dark',
  },
  {
    id: 'mesh',
    label: 'Mesh',
    description: 'Indigo, teal and rose, melted together.',
    kind: 'picture',
    colour: '#38385f',
    tone: 'dark',
  },
  {
    id: 'origami',
    label: 'Origami',
    description: 'Folded paper, lit like a studio.',
    kind: 'picture',
    colour: '#28264f',
    tone: 'dark',
  },
  {
    id: 'chalk',
    label: 'Chalkboard',
    description: 'A deep green board, faintly marked.',
    kind: 'picture',
    colour: '#082630',
    tone: 'dark',
  },
  {
    id: 'paper',
    label: 'Paper',
    description: 'Warm and bright, for a light room.',
    kind: 'picture',
    colour: '#f7ede5',
    tone: 'light',
  },
] as const;

/** What a class gets before anybody chooses, and what the platform's own page uses. */
export const DEFAULT_BACKGROUND = 'code';
export const PLATFORM_BACKGROUND = 'code';

export function findBackground(id: string | undefined): Background | undefined {
  return BACKGROUNDS.find((background) => background.id === id);
}

/** Where a shipped picture lives, at the width a screen of this size will want. */
export function backgroundSrc(id: string, width: 1080 | 1920): string {
  return `/backgrounds/${id}-${String(width)}.webp`;
}
